import { env } from './env.js';

// the team access design doc (CommitMind) — dashboard-side fetch helpers for the
// workspace invitation flow. Mirrors the api endpoints under
// /api/invitations.

export type WorkspaceInviteRole = 'admin' | 'member';
export type BrandGrantRole = 'manager' | 'editor' | 'viewer' | 'designer';

export interface InvitationBrandGrant {
  brandId: string;
  role: BrandGrantRole;
}

export interface InvitationResponse {
  id: string;
  email: string;
  workspaceRole: WorkspaceInviteRole;
  brandGrants: InvitationBrandGrant[];
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}

export interface InvitationPreview {
  invitationId: string;
  organizationName: string;
  inviterName: string;
  inviterEmail: string;
  invitedEmail: string;
  workspaceRole: WorkspaceInviteRole;
  brandGrants: Array<InvitationBrandGrant & { name: string; slug: string }>;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
}

export interface CreateInvitationInput {
  email: string;
  workspaceRole: WorkspaceInviteRole;
  brandGrants?: InvitationBrandGrant[];
  expiresInDays?: number;
}

export interface CreateInvitationResponse {
  invitation: InvitationResponse;
  acceptUrl: string;
  /** Raw token — only returned at create-time so the inviter can copy
   *  the URL when the email is slow. Treat as a secret; never log. */
  rawToken: string;
}

export class InvitationApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message?: string,
  ) {
    super(message ?? code);
  }
}

async function readJsonOrThrow<T>(res: Response, path: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  let body: { error?: string; message?: string } = {};
  try {
    body = await res.json();
  } catch {
    // Non-JSON error body — fall through to a generic message.
  }
  throw new InvitationApiError(
    body.error ?? `http_${res.status}`,
    res.status,
    body.message ?? `${res.status} from ${path}`,
  );
}

export async function listInvitations(): Promise<InvitationResponse[]> {
  const res = await fetch(`${env.API_URL}/api/invitations`, { credentials: 'include' });
  const body = await readJsonOrThrow<{ invitations: InvitationResponse[] }>(
    res,
    '/api/invitations',
  );
  return body.invitations;
}

export async function createInvitation(
  input: CreateInvitationInput,
): Promise<CreateInvitationResponse> {
  const res = await fetch(`${env.API_URL}/api/invitations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJsonOrThrow<CreateInvitationResponse>(res, 'POST /api/invitations');
}

export async function revokeInvitation(id: string): Promise<InvitationResponse> {
  const res = await fetch(`${env.API_URL}/api/invitations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const body = await readJsonOrThrow<{ invitation: InvitationResponse }>(
    res,
    `DELETE /api/invitations/${id}`,
  );
  return body.invitation;
}

export async function resendInvitation(id: string): Promise<CreateInvitationResponse> {
  const res = await fetch(`${env.API_URL}/api/invitations/${encodeURIComponent(id)}/resend`, {
    method: 'POST',
    credentials: 'include',
  });
  return readJsonOrThrow<CreateInvitationResponse>(res, `POST /api/invitations/${id}/resend`);
}

export async function fetchInvitationPreview(token: string): Promise<InvitationPreview | null> {
  const res = await fetch(
    `${env.API_URL}/api/invitations/preview?token=${encodeURIComponent(token)}`,
    { credentials: 'include' },
  );
  if (res.status === 404) return null;
  const body = await readJsonOrThrow<{ preview: InvitationPreview }>(
    res,
    '/api/invitations/preview',
  );
  return body.preview;
}

export interface AcceptInvitationResponse {
  organizationId: string;
  workspaceRole: WorkspaceInviteRole;
  brandIds: string[];
}

export async function acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
  const res = await fetch(`${env.API_URL}/api/invitations/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  return readJsonOrThrow<AcceptInvitationResponse>(res, 'POST /api/invitations/accept');
}
